import React, { useState } from "react";

import { isEmpty } from "lodash-es";
import { useController, useForm, useFormState } from "react-hook-form";

import {
  taktileInternalPrefix,
  validateNotStartsWithInternalPrefix,
} from "src/api/constants";
import { KeyTypeSelector } from "src/apiKeys/KeyTypeSelector";
import { Label } from "src/base-components/Label";
import {
  ApiKeyType,
  ServiceAccountOAuthClientCreated,
} from "src/clients/taktile-api";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Input } from "src/design-system/Input";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { useCreateOAuthClient } from "src/oauthClients/api/queries";
import {
  KEY_LIFETIME_MAX_DAYS,
  keyLifetimeDaysError,
} from "src/oauthClients/constants";
import { KeyMaterialError } from "src/oauthClients/keyMaterial";
import { PublicKeyFieldset } from "src/oauthClients/PublicKeyFieldset";
import {
  KeySupplyMode,
  resolveKeyMaterial,
} from "src/oauthClients/resolveKeyMaterial";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

export type CreatedOAuthClient = {
  client: ServiceAccountOAuthClientCreated;
  privateKeyPem?: string;
};

type OAuthClientInputs = {
  name: string;
  keyTypes: ApiKeyType[];
  keyLifetimeDays: string;
};

type Props = {
  open: boolean;
  organizationId: string;
  workspaceId: string;
  /** Empty while the workspace has no base URL to build the endpoint from. */
  proxyTokenEndpoint: string;
  onClose: () => void;
  onCreated: (created: CreatedOAuthClient) => void;
};

export const CreateOAuthClientModal: React.FC<Props> = ({
  open,
  organizationId: orgId,
  workspaceId: wsId,
  proxyTokenEndpoint,
  onClose,
  onCreated,
}) => {
  const createOAuthClient = useCreateOAuthClient(orgId, wsId);
  // The endpoint is a required field the form never shows, so without it the
  // request can only come back as a 422 on a field the customer cannot see.
  const canCreate = Boolean(proxyTokenEndpoint);
  const [keySupplyMode, setKeySupplyMode] = useState<KeySupplyMode>("generate");
  const [pastedKey, setPastedKey] = useState("");
  const [keyError, setKeyError] = useState<string>();

  const { control, register, handleSubmit, reset } = useForm<OAuthClientInputs>(
    {
      mode: "onChange",
      defaultValues: {
        name: "",
        keyTypes: [ApiKeyType.SANDBOX],
        keyLifetimeDays: "",
      },
    },
  );

  // Read through useFormState, not by destructuring useForm().formState: this
  // folder is compiled, and the compiler memoizes the destructured read so the
  // field errors stop re-rendering (RHF#12618).
  const { errors, isSubmitting, isDirty, isValid } = useFormState({ control });

  const { field: keyTypesField } = useController({
    control,
    name: "keyTypes",
    rules: { required: true },
  });

  const handleKeySupplyModeChange = (mode: KeySupplyMode) => {
    setKeySupplyMode(mode);
    setKeyError(undefined);
  };

  const handlePastedKeyChange = (value: string) => {
    setPastedKey(value);
    setKeyError(undefined);
  };

  const handleAfterLeave = () => {
    reset();
    setKeySupplyMode("generate");
    setPastedKey("");
    setKeyError(undefined);
  };

  const submit = handleSubmit(async (input: OAuthClientInputs) => {
    if (createOAuthClient.isPending || !canCreate) {
      return;
    }
    setKeyError(undefined);

    try {
      const { publicJwk, privateKeyPem } = await resolveKeyMaterial(
        keySupplyMode,
        pastedKey,
      );
      const client = (
        await createOAuthClient.mutateAsync({
          name: input.name,
          key_types: input.keyTypes,
          public_jwk: publicJwk,
          proxy_token_endpoint: proxyTokenEndpoint,
          ...(input.keyLifetimeDays
            ? { key_lifetime_days: Number(input.keyLifetimeDays) }
            : {}),
        })
      ).data;
      onCreated({ client, privateKeyPem });
    } catch (error) {
      if (error instanceof KeyMaterialError) {
        setKeyError(error.message);
        return;
      }
      logger.error(`Error when creating OAuth client: ${error}`);
      toastActions.failure({
        title: "Failed to create OAuth client",
        description: errorMessage(error),
      });
      return;
    }
    onClose();
  });

  return (
    <Modal afterLeave={handleAfterLeave} open={open} onClose={onClose}>
      <Modal.Header description="A machine client that mints workspace tokens with the client credentials grant, authenticated with a key pair you control.">
        Create OAuth client
      </Modal.Header>
      <form onSubmit={submit}>
        <Modal.Content>
          <div className="flex flex-col gap-5">
            {!canCreate && (
              <Callout dataLoc="oauth-client-no-base-url" variant="warning">
                This workspace has no base URL yet, so the token endpoint an
                OAuth client authenticates against cannot be built. Contact
                Taktile support to have one assigned.
              </Callout>
            )}
            <div className="flex flex-col gap-2">
              <Label mb="mb-0" required>
                Client name
              </Label>
              <Input
                aria-label="Client name"
                error={Boolean(errors.name?.message)}
                placeholder="Name your OAuth client"
                {...register("name", {
                  required: true,
                  validate: validateNotStartsWithInternalPrefix(
                    `OAuth client name cannot start with ${taktileInternalPrefix}`,
                  ),
                })}
              />
              {Boolean(errors.name?.message) && (
                <ErrorHint>{errors.name?.message}</ErrorHint>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label mb="mb-0" required>
                Permissions
              </Label>
              <KeyTypeSelector
                value={keyTypesField.value}
                onChange={keyTypesField.onChange}
              />
            </div>

            <PublicKeyFieldset
              error={keyError}
              mode={keySupplyMode}
              pastedKey={pastedKey}
              onModeChange={handleKeySupplyModeChange}
              onPastedKeyChange={handlePastedKeyChange}
            />

            <div className="flex flex-col gap-2">
              <Label mb="mb-0">Key lifetime in days</Label>
              <Input
                aria-label="Key lifetime in days"
                error={Boolean(errors.keyLifetimeDays?.message)}
                placeholder={`${KEY_LIFETIME_MAX_DAYS}`}
                type="number"
                {...register("keyLifetimeDays", {
                  validate: (value) => keyLifetimeDaysError(value) ?? true,
                })}
              />
              {Boolean(errors.keyLifetimeDays?.message) && (
                <ErrorHint>{errors.keyLifetimeDays?.message}</ErrorHint>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="create-oauth-client-submit"
              isDisabled={
                !canCreate ||
                !(isDirty && isValid) ||
                !isEmpty(errors) ||
                isSubmitting
              }
              htmlType="submit"
              isLoading={isSubmitting}
              variant="primary"
            >
              Create OAuth client
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
